import { ExternalLink, MapPin, Play } from 'lucide-react';

interface LinkPreviewProps {
  content: string;
  isOwn: boolean;
}

interface DetectedLink {
  url: string;
  type: 'tiktok' | 'apple_maps' | 'google_maps' | 'generic';
  label: string;
}

const URL_REGEX = /https?:\/\/[^\s<>]+/gi;

function detectLinks(text: string): DetectedLink[] {
  const urls = text.match(URL_REGEX);
  if (!urls) return [];

  return urls.map(url => {
    const lower = url.toLowerCase();
    if (lower.includes('tiktok.com') || lower.includes('vm.tiktok.com')) {
      return { url, type: 'tiktok' as const, label: 'TikTok' };
    }
    if (lower.includes('maps.apple.com')) {
      return { url, type: 'apple_maps' as const, label: 'Apple Maps' };
    }
    if (lower.includes('google.com/maps') || lower.includes('goo.gl/maps') || lower.includes('maps.app.goo.gl')) {
      return { url, type: 'google_maps' as const, label: 'Google Maps' };
    }
    return null;
  }).filter(Boolean) as DetectedLink[];
}

export function LinkPreview({ content, isOwn }: LinkPreviewProps) {
  const links = detectLinks(content);
  if (links.length === 0) return null;

  return (
    <div className="mt-2 space-y-1.5">
      {links.map((link, i) => (
        <a
          key={i}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-2.5 px-3 py-2 rounded-xl transition-colors ${
            isOwn
              ? 'bg-white/10 hover:bg-white/20'
              : 'bg-muted/60 hover:bg-muted/80'
          }`}
        >
          {/* Icon */}
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
            link.type === 'tiktok'
              ? 'bg-black text-white'
              : link.type === 'apple_maps'
              ? 'bg-green-500/20 text-green-600'
              : link.type === 'google_maps'
              ? 'bg-blue-500/20 text-blue-600'
              : 'bg-muted'
          }`}>
            {link.type === 'tiktok' && <Play className="h-4 w-4" />}
            {(link.type === 'apple_maps' || link.type === 'google_maps') && <MapPin className="h-4 w-4" />}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold ${isOwn ? 'text-white/90' : 'text-foreground'}`}>
              {link.label}
            </p>
            <p className={`text-[10px] truncate ${isOwn ? 'text-white/50' : 'text-muted-foreground/70'}`}>
              {link.url.replace(/^https?:\/\//, '').slice(0, 40)}…
            </p>
          </div>

          <ExternalLink className={`h-3.5 w-3.5 shrink-0 ${isOwn ? 'text-white/40' : 'text-muted-foreground/40'}`} />
        </a>
      ))}
    </div>
  );
}

export { detectLinks };
